import { PrizeForPrecisionYear } from './../../world-ranking-statistics/prize-for-precision/classes/prize-for-precision';
import {Injectable} from '@angular/core';

// Structs.
import {CategoryCardinalityStruct} from '../util/category-cardinality.struct';
import {IdLabelStruct} from '../util/id-label.struct';

// Classes.
import {LoggedComponent} from '../logger/logged-component';
import {CategoryItem} from '../fetching/types/category.item';
import {CountryNameItem} from '../fetching/types/country-name.item';


// Providers
import {LoggerService} from '../logger/logger-service';
import { WCupSeasonItem } from '../fetching/types/wcup-season.item';
import { ComboStoreComponent } from 'src/app/common-page-components/stores/filters-store/filters-store';



export type LevelGroupNames = 'international' | 'continental' | 'national' | 'ALL';


export type FlagSize =
  "small" | "medium" | "large";


export type DateFormatType =
  |'webservice'
  | 'widget'
  | 'short'
  | 'monthYearShort'
  | 'year'
  | 'long';


@Injectable({
  providedIn: 'root'

}
)
export class WaHelperService extends LoggedComponent {

  static readonly monthNames = ["January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];



  /**
   * List of level info: code and name.
   */
  static readonly levelInfo: {[index: number]: IdLabelStruct} = {
    1: new IdLabelStruct(1, 'Olympic Games'),
    2: new IdLabelStruct(2, 'World Championships'),
    3: new IdLabelStruct(3, 'World Cup'),
    4: new IdLabelStruct(4, 'Multisport World Event'),
    5: new IdLabelStruct(5, 'Multisport Continental Event'),
    6: new IdLabelStruct(6, 'Continental Games'),
    7: new IdLabelStruct(7, 'Continental Championships'),
    8: new IdLabelStruct(8, 'Other International Event'),
    9: new IdLabelStruct(9, 'National Championships'),
    10: new IdLabelStruct(10, 'National Competition')
  };

  /**
   * List of division info: code and name.
   */
  static readonly divisionInfo: {[index: number]: IdLabelStruct} = {
    1: new IdLabelStruct(1, 'Recurve'),
    2: new IdLabelStruct(2, 'Compound'),
    3: new IdLabelStruct(3, 'Barebow'),
    4: new IdLabelStruct(4, 'Longbow'),
    5: new IdLabelStruct(5, 'Instinctive bow'),
    6: new IdLabelStruct(6, 'Target Recurve Bow'),
    7: new IdLabelStruct(7, 'Conventional Flight Bow'),
    8: new IdLabelStruct(8, 'American Longbow'),
    9: new IdLabelStruct(9, 'English Longbow'),
    10: new IdLabelStruct(10, 'Compound Flight Bow'),
    11: new IdLabelStruct(11, 'Compound Target Bow'),
    12: new IdLabelStruct(12, 'Foot Bow'),
  };

  /**
   * List of discipline info: code and name.
   */
  static readonly disciplineInfo: {[index: number]: IdLabelStruct} = {
    1: new IdLabelStruct(1, 'Outdoor Archery'),
    2: new IdLabelStruct(2, 'Indoor Archery'),
    3: new IdLabelStruct(3, 'Field Archery'),
    4: new IdLabelStruct(4, '3D Archery'),
    5: new IdLabelStruct(5, 'Ski Archery'),
    6: new IdLabelStruct(6, 'Run Archery'),
    7: new IdLabelStruct(7, 'Clout Archery'),
    8: new IdLabelStruct(8, 'Flight Archery'),
    10: new IdLabelStruct(10, 'N/A')
  };

  /*****************************************************************************
   *  Data in memory
   ****************************************************************************/
  protected cateogryById: {[index: number]: CategoryItem} = {};
  protected cateogryByCode: {[index: string]: CategoryItem} = {};

  protected countryNamesByNoc: {[index: string]: CountryNameItem} = {};

  public constructor(protected logger: LoggerService) {
    super(logger);
  }

  public addCountryNameItem(item: CountryNameItem) {
    this.countryNamesByNoc[item.noc] = item;
  }

  public getCountryNamesInfo(noc: string): CountryNameItem {
    return this.countryNamesByNoc[noc];
  }

  public addCategoryItem(item: CategoryItem) {
    this.cateogryByCode[item.code] = this.cateogryById[item.id] = item;
  }

  public getCategoryInfoById(id: number): CategoryItem {
    return this.cateogryById[id];
  }

  public getCategoryInfoByCode(code: string): CategoryItem {
    return this.cateogryByCode[code];
  }

  /**
   * Returns the coutry name given a noc.
   */
  public nocToCountryName(noc: string, nameType: "short" | "long" = "short") {
    let countryName = 'Unknown';

    if (typeof this.countryNamesByNoc[noc] !== 'undefined') {
      countryName = this.countryNamesByNoc[noc][nameType];
    }
    return countryName;
  }

  /**
   * Returns the coutry flag pic url given a noc.
   */
  public nocToFlagUrl(noc: string, size: FlagSize = 'small') {
    let sizeCode: string;

    switch (size) {
      case 'small':
        sizeCode = 'S';
        break;
      case 'medium':
        sizeCode = 'M';
        break;
      case 'large':
        sizeCode = 'L';
        break;
    }

    return `https://extranet.worldarchery.sport/Flags/${noc}-${sizeCode}.png`;
  }

  public getLevelListInfo(): IdLabelStruct[] {
    let levelsInfo: IdLabelStruct[] = [];
    Object.keys(WaHelperService.levelInfo).forEach(
      key => levelsInfo.push(WaHelperService.levelInfo[key])
    );
    return levelsInfo;
  }

  /**
   * Returns division info given division code.
   */
  public getLevelInfo(levelId: number): IdLabelStruct {
    let levelInfo = new IdLabelStruct(levelId, 'Unknown');
    if (WaHelperService.levelInfo[levelId]) {
      levelInfo = WaHelperService.levelInfo[levelId];
    }
    return levelInfo;
  }

  public getLevelIdByLevelGroupName(levelGroup: LevelGroupNames): number[] {
    let levels: number[];

    switch (levelGroup) {
      case 'international':
        levels = [1, 2, 3, 4];
        break;
      case 'continental':
        levels = [5, 6, 7];
        break;
      case 'national':
        levels = [8, 9, 10];
        break;
      case 'ALL':
        levels = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];
        break;
    }
    return levels;
  }

  public getDivisionListInfo(): IdLabelStruct[] {
    let divisionsInfo: IdLabelStruct[] = [];
    Object.keys(WaHelperService.divisionInfo).forEach(
      key => divisionsInfo.push(WaHelperService.divisionInfo[key])
    );
    return divisionsInfo;
  }

  /**
   * Returns division info given division code.
   */
  public getDivisionInfo(divCode: number): IdLabelStruct {
    let divisionInfo = new IdLabelStruct(divCode, 'Unknown');
    if (WaHelperService.divisionInfo[divCode]) {
      divisionInfo = WaHelperService.divisionInfo[divCode];
    }
    return divisionInfo;
  }

  /**
   * Returns the label of a division given its code
   */
  public divisionCodeToLabel(divCode: number): string {
    return this.getDivisionInfo(divCode).label;
  }

  public demuxCategoryAndCardinality(rawValue: string): CategoryCardinalityStruct {
    let demuxed = null;
    if (rawValue) {
      let catComponents = rawValue.split('-');
      demuxed = new CategoryCardinalityStruct(catComponents[0], catComponents[1] === 't');
    }
    return demuxed;
  }

  public muxCategoryAndCardinality(value: CategoryCardinalityStruct): string {
    let muxed = null;
    if (value) {
      let components = [value.catCode, value.isTeam ? 't' : 'i'];
      muxed = components.filter(v => v).join('-');
    }
    return muxed;
  }

  public formatDate(date: Date, format: DateFormatType = 'long'): string {
    let formatedDate: string;
    let dateMonthNumber = date.getMonth() + 1;
    let monthLabel = (dateMonthNumber < 10 ? '0' : '') + dateMonthNumber;

    let dateDayNumber = date.getDate();
    let dateDayLabel = (dateDayNumber < 10 ? '0' : '') + dateDayNumber;


    switch (format) {
      case 'webservice':
        formatedDate = formatedDate = `${date.getFullYear()}${monthLabel}${dateDayLabel}`;
        break;
      case 'widget':
        formatedDate = formatedDate = `${date.getFullYear()}-${monthLabel}-${dateDayLabel}`;
        break;
      case 'short':
        formatedDate = formatedDate = `${dateDayLabel}/${monthLabel}/${date.getFullYear()}`;
        break;
      case 'monthYearShort':
        formatedDate = `${date.getMonth() + 1}/${date.getFullYear()}`;
        break;
      case 'year':
        formatedDate = `${date.getFullYear()}`
        break;
      case 'long':
      default:
        formatedDate = `${dateDayLabel} ${WaHelperService.monthNames[date.getMonth()]} ${date.getFullYear()}`;
        break;

    }

    return formatedDate;
  }

  public static formatDateRange(from: Date, to: Date): string {
    let dateString: string;
    let matching = 0;
    if (from.getFullYear() === to.getFullYear()) {
      matching++;
      if (from.getMonth() === to.getMonth()) {
        matching++;
        if (from.getDate() === to.getDate()) {
          matching++;
        }
      }
    }

    switch (matching) {
      case 0:
        dateString = `${from.getDate()} ${WaHelperService.monthNames[from.getMonth()]} ${from.getFullYear()} - ${to.getDate()} ${WaHelperService.monthNames[to.getMonth()]} ${to.getFullYear()}`
        break;
      case 1:
        dateString = `${from.getDate()} ${WaHelperService.monthNames[from.getMonth()]} - ${to.getDate()} ${WaHelperService.monthNames[to.getMonth()]} ${to.getFullYear()}`
        break;
      case 2:
        dateString = `${from.getDate()}-${to.getDate()} ${WaHelperService.monthNames[to.getMonth()]} ${to.getFullYear()}`
        break;
      case 3:
        dateString = `${from.getDate()} ${WaHelperService.monthNames[from.getMonth()]} ${from.getFullYear()}`
        break;
    }

    return dateString;
  }

  /**
   * Method for get the combo season item by current year.
   *
   * @returns {PrizeForPrecisionYear}
   * @param PrizeForPrecisionYear[]
   * @public
   * @returns
   */
  public static getComboSeasonItemByCurrentYear(comboSeason: PrizeForPrecisionYear[]): PrizeForPrecisionYear {
    // const currentYear = new Date().getFullYear()
    const currentYear = 2026; // Forced  to  2026 for take the last season #Task: https://app.asana.com/1/44511833986982/project/1207725188900996/task/1210705958663285
    if (comboSeason) {
      return comboSeason.find(value => value.Year === currentYear);
    }else{
      return {Year: currentYear, Discipline: 2, Series: '', PrecisionPrize: false} as PrizeForPrecisionYear;
    }
  }
}
